import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import {
  accountRequiredMessage,
  type ContractService,
  paxRoute,
  unauthorizedMessage,
} from "../../common";
import {
  FriendProfileIdParams,
  FriendRelationshipResponse,
  FriendSearchQuery,
  FriendSummaryResponse,
  ListFriendsQuery,
  ListFriendsResponse,
  ListMutualFriendsQuery,
  ListMutualFriendsResponse,
  SearchFriendsResponse,
  SearchProfilesResponse,
  SendFriendRequestBody,
  SendFriendRequestResponse,
} from "./schemas";

export const service = {
  group: "friends",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Friends API",
  description: "Friend requests, friendships, mutual friends, and relationship-aware search.",
} as const satisfies ContractService;

const accountErrors = {
  UNAUTHORIZED: { message: unauthorizedMessage },
  FORBIDDEN: { message: accountRequiredMessage },
} as const;

const invalidProfileId = {
  BAD_REQUEST: { message: "profileId must be a valid UUID." },
} as const;

const noSuchProfile = {
  NOT_FOUND: { message: "No account found." },
} as const;

export const summary = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/friends/v1alpha1/summary",
      operationId: "friends.summary.get",
      summary: "Get my friend and request counts.",
      description:
        "Returns the caller's accepted friend count and the number of pending requests received and sent.",
      inputStructure: "detailed",
      successDescription: "The caller's counts.",
    }),
  )
  .errors({ ...accountErrors })
  .output(FriendSummaryResponse);

export const list = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/friends/v1alpha1",
      operationId: "friends.list",
      summary: "List my friends or pending requests.",
      description:
        "Pages through the caller's accepted friends, the requests waiting on them, or the requests they have sent, newest first.",
      inputStructure: "detailed",
      successDescription: "One page of the requested list.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message: "tab must be friends, received, or sent; cursor must come from a previous page.",
    },
    ...accountErrors,
  })
  .input(z.object({ query: ListFriendsQuery }))
  .output(ListFriendsResponse);

export const sendRequest = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/friends/v1alpha1/requests",
      operationId: "friends.requests.create",
      summary: "Send a friend request.",
      description:
        "Creates a pending request to another connected profile. If that profile already asked the caller, nothing is written and the response says to accept instead.",
      inputStructure: "detailed",
      successDescription: "What sending the request did.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message: "profileId must be a valid UUID and not the caller's own.",
    },
    ...accountErrors,
    ...noSuchProfile,
  })
  .input(z.object({ body: SendFriendRequestBody }))
  .output(SendFriendRequestResponse);

export const cancelRequest = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/friends/v1alpha1/requests/{profileId}",
      operationId: "friends.requests.delete",
      summary: "Cancel a friend request I sent.",
      description:
        "Withdraws the caller's pending request to the named profile. Succeeds when there is nothing to withdraw.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The friend request was cancelled.",
    }),
  )
  .errors({
    ...invalidProfileId,
    ...accountErrors,
    CONFLICT: {
      message: "That request was accepted before it could be cancelled; remove the friend instead.",
    },
  })
  .input(z.object({ params: FriendProfileIdParams }));

export const acceptRequest = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/friends/v1alpha1/requests/{profileId}/accept",
      operationId: "friends.requests.accept",
      summary: "Accept a friend request.",
      description:
        "Turns the named profile's pending request to the caller into a friendship and increments both friend counts.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The friendship was created.",
    }),
  )
  .errors({
    ...invalidProfileId,
    ...accountErrors,
    NOT_FOUND: {
      message: "There is no friend request from that profile to accept.",
    },
  })
  .input(z.object({ params: FriendProfileIdParams }));

export const declineRequest = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/friends/v1alpha1/requests/{profileId}/decline",
      operationId: "friends.requests.decline",
      summary: "Decline a friend request.",
      description:
        "Removes the named profile's pending request to the caller. Succeeds when there is nothing to decline. The requester is not told.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The friend request was declined.",
    }),
  )
  .errors({
    ...invalidProfileId,
    ...accountErrors,
    CONFLICT: {
      message: "That request was accepted before it could be declined; remove the friend instead.",
    },
  })
  .input(z.object({ params: FriendProfileIdParams }));

export const remove = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/friends/v1alpha1/{profileId}",
      operationId: "friends.delete",
      summary: "Remove a friend.",
      description:
        "Ends the friendship with the named profile and decrements both friend counts. Succeeds when there is no friendship.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The friendship was removed.",
    }),
  )
  .errors({ ...invalidProfileId, ...accountErrors })
  .input(z.object({ params: FriendProfileIdParams }));

export const getRelationship = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/friends/v1alpha1/relationships/{profileId}",
      operationId: "friends.relationships.get",
      summary: "Get my relationship to a profile.",
      description:
        "Returns where the caller stands with one profile and how many friends they share, for the friend control on a profile page.",
      inputStructure: "detailed",
      successDescription: "The relationship and mutual friend count.",
    }),
  )
  .errors({ ...invalidProfileId, ...accountErrors, ...noSuchProfile })
  .input(z.object({ params: FriendProfileIdParams }))
  .output(FriendRelationshipResponse);

export const listMutual = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/friends/v1alpha1/{profileId}/mutual-friends",
      operationId: "friends.mutualFriends.list",
      summary: "List friends I share with a profile.",
      description:
        "Pages through profiles that are friends with both the caller and the named profile, and returns the total.",
      inputStructure: "detailed",
      successDescription: "One page of mutual friends.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message:
        "profileId must be a valid UUID other than the caller's own; cursor must come from a previous page.",
    },
    ...accountErrors,
    ...noSuchProfile,
  })
  .input(z.object({ params: FriendProfileIdParams, query: ListMutualFriendsQuery }))
  .output(ListMutualFriendsResponse);

export const searchFriends = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "search" }),
    openapi({
      method: "GET",
      path: "/api/friends/v1alpha1/search",
      operationId: "friends.search",
      summary: "Search within my friends.",
      description:
        "Finds the caller's accepted friends by profile id or username prefix. Returns at most ten entries and no total.",
      inputStructure: "detailed",
      successDescription: "Matching friends.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message: "q must be a profile id or the start of a username, 2–64 characters.",
    },
    ...accountErrors,
  })
  .input(z.object({ query: FriendSearchQuery }))
  .output(SearchFriendsResponse);

export const searchProfiles = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "search" }),
    openapi({
      method: "GET",
      path: "/api/friends/v1alpha1/relationships/search",
      operationId: "friends.relationships.search",
      summary: "Search profiles with my relationship to each.",
      description:
        "Finds connected profiles by profile id or username prefix and tags each with the caller's relationship, so a result row knows whether to offer Add, Cancel, Accept, or nothing. Returns at most ten entries and no total.",
      inputStructure: "detailed",
      successDescription: "Matching profiles with relationships.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message: "q must be a profile id or the start of a username, 2–64 characters.",
    },
    ...accountErrors,
  })
  .input(z.object({ query: FriendSearchQuery }))
  .output(SearchProfilesResponse);
